import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { buildAuthUrl, buildAuthCallbackUrl, sanitizeReturnTo, getPendingAuth, setPendingAuth, clearPendingAuth, getAuthRetryAt } from "@/lib/authFlow";
import { toast } from "sonner";
import { Target, Shield, ShieldCheck, Wallet, Mail, Lock, Eye, EyeOff, Check, X, Loader2, AlertTriangle } from "lucide-react";
import { authApi } from "@/services/api";
import { supabase } from "@/integrations/supabase/client";
import { isAllowedEmail } from "@/config/constants";
import { authSchema } from "@/lib/validations";
import { trackFunnelEvent } from "@/lib/funnelAnalytics";

const Auth = () => {
  const { user, loading: authLoading, isAuthenticated, assuranceStatus, isPasswordRecovery, completePasswordRecovery, refreshAssurance, signOut } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [pending, setPending] = useState(getPendingAuth);
  const legacyFrom = location.state?.from;
  const returnTo = sanitizeReturnTo(searchParams.get("returnTo")
    ?? (typeof legacyFrom === "string" ? legacyFrom : legacyFrom?.pathname) ?? pending?.returnTo, "/post");
  const mode = searchParams.get("mode") === "signin" ? "in" : "up";
  const [email, setEmail] = useState(() => pending?.email ?? "");
  const [showConfirmation, setShowConfirmation] = useState(() => !!pending?.email && pending.kind !== "signin" && mode !== "in");
  const [feedback, setFeedback] = useState("");
  const [now, setNow] = useState(Date.now);
  const [retryAt, setRetryAt] = useState(0);
  const [callbackError, setCallbackError] = useState(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1));
    return searchParams.has("error") || searchParams.has("error_code") || hash.has("error") || hash.has("error_code");
  });
  const requestInFlight = useRef(false);
  const notifyLogin = useRef(false);
  const resendSeconds = Math.max(0, Math.ceil(((pending?.resendAvailableAt ?? 0) - now) / 1000));
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const showMFADialog = !!user && assuranceStatus === "mfa_required";
  const [mfaCode, setMFACode] = useState("");
  const [mfaLoading, setMFALoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const isPasswordResetMode = isPasswordRecovery || searchParams.get("reset") === "true";
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [emailWarning, setEmailWarning] = useState("");
  const isVerifyingMFA = useRef(false);
  const confirmedFired = useRef(false);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    const sync = () => setPending(getPendingAuth());
    window.addEventListener("storage", sync);
    return () => { window.clearInterval(timer); window.removeEventListener("storage", sync); };
  }, []);

  useEffect(() => {
    if (pending && pending.expiresAt <= now) {
      clearPendingAuth();
      setPending(null);
      setShowConfirmation(false);
      setEmail("");
      setFeedback("This confirmation reminder expired. Request a new link or sign in.");
    }
  }, [pending, now]);

  useEffect(() => {
    if (isAuthenticated && !authLoading && !loading && !isPasswordResetMode && !showResetPassword && !callbackError) {
      if (pending?.kind === "signup" && !confirmedFired.current) {
        confirmedFired.current = true;
        trackFunnelEvent("email_confirmed", { source: "auth" });
      }
      clearPendingAuth();
      if (notifyLogin.current && user?.id) {
        notifyLogin.current = false;
        // The generated database types omit this existing best-effort notification RPC.
        const sendNotification = supabase.rpc.bind(supabase) as unknown as (
          name: "send_login_notification", args: { p_user_id: string },
        ) => PromiseLike<unknown>;
        void Promise.resolve(sendNotification("send_login_notification", { p_user_id: user.id })).catch(() => {});
      }
      nav(returnTo, { replace: true });
    }
  }, [isAuthenticated, authLoading, loading, isPasswordResetMode, showResetPassword, callbackError, nav, returnTo, user?.id]);

  const savePending = (kind: "signup" | "signin" | "recovery", address: string, resendAvailableAt = pending?.resendAvailableAt ?? 0) => {
    const next = setPendingAuth({ kind, email: address, returnTo, createdAt: pending?.createdAt, resendAvailableAt });
    setPending(next);
    setNow(Date.now());
    return next;
  };

  const isRateLimited = (error: unknown) => {
    const value = error as { status?: number; code?: string } | null;
    return value?.status === 429 || /rate_limit|too_many/.test(value?.code ?? "");
  };

  // Password strength calculator
  const calculatePasswordStrength = (pwd: string) => {
    let strength = 0;
    if (pwd.length >= 8) strength += 1;
    if (pwd.length >= 12) strength += 1;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength += 1;
    if (/\d/.test(pwd)) strength += 1;
    if (/[^a-zA-Z0-9]/.test(pwd)) strength += 1;
    return strength;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pwd = e.target.value;
    setPassword(pwd);
    setPasswordStrength(calculatePasswordStrength(pwd));
  };

  const submit = async (mode: "in" | "up") => {
    if (requestInFlight.current || authLoading || user || Date.now() < retryAt) return;
    if (mode === "up" && Date.now() < (pending?.resendAvailableAt ?? 0)) return;
    const parsed = authSchema.safeParse({ email, password });
    if (!parsed.success) { setFeedback(parsed.error.issues[0].message); return; }
    const address = parsed.data.email;
    if (mode === "up" && !isAllowedEmail(address)) {
      setFeedback("This email domain is not allowed. Please use a valid email address.");
      return;
    }
    if (mode === "up") trackFunnelEvent("signup_requested", { source: "auth", intent: "post" });
    requestInFlight.current = true;
    setLoading(true);
    setFeedback("");
    setCallbackError(false);
    const attempt = savePending(mode === "up" ? "signup" : "signin", address,
      mode === "up" ? getAuthRetryAt() : pending?.resendAvailableAt);
    try {
      if (mode === "up") {
        const { error } = await supabase.auth.signUp({
          email: address, password: parsed.data.password,
          options: { emailRedirectTo: buildAuthCallbackUrl(returnTo) },
        });
        if (error && !["user_already_exists", "email_exists"].includes(error.code ?? "")) throw error;
        trackFunnelEvent("confirmation_sent", { source: "auth", mode: "signup" });
        setShowConfirmation(true);
        setPassword("");
        setEmailWarning("");
      } else {
        notifyLogin.current = true;
        const { error } = await authApi.signIn(address, parsed.data.password);
        if (error) {
          if ((error as { code?: string }).code === "email_not_confirmed") {
            savePending("signup", address);
            setShowConfirmation(true);
            setPassword("");
            return;
          }
          throw error;
        }
        setPassword("");
        // The provider checks the actual AAL before the navigation effect can run.
      }
    } catch (error) {
      trackFunnelEvent("auth_error", {
        source: "auth",
        mode: mode === "up" ? "signup" : "signin",
        stage: mode === "up" ? "signup" : "signin",
        error_category: isRateLimited(error) ? "rate_limited" : "unknown",
      });
      if (isRateLimited(error)) {
        const deadline = getAuthRetryAt(error);
        setRetryAt(deadline);
        setPending(setPendingAuth({ ...attempt, resendAvailableAt: deadline }));
        setNow(Date.now());
        setFeedback("Too many requests. Please wait before trying again.");
      } else {
        setFeedback(mode === "up" ? "We couldn't request a confirmation email. Please try again later."
          : "Unable to sign in. Check your email and password, confirm your email if needed, or reset your password.");
      }
    } finally {
      requestInFlight.current = false;
      setLoading(false);
    }
  };

  const requestEmail = async (kind: "signup" | "recovery", address?: string) => {
    if (requestInFlight.current || Date.now() < (pending?.resendAvailableAt ?? 0)) return;
    const parsed = authSchema.shape.email.safeParse(address ?? (kind === "signup" ? pending?.email : email));
    if (!parsed.success) { setFeedback("Please enter a valid email address."); return; }
    requestInFlight.current = true;
    setResetLoading(true);
    const attempt = savePending(kind, parsed.data, getAuthRetryAt());
    try {
      const { error } = kind === "signup"
        ? await supabase.auth.resend({ type: "signup", email: parsed.data, options: { emailRedirectTo: buildAuthCallbackUrl(returnTo) } })
        : await supabase.auth.resetPasswordForEmail(parsed.data, { redirectTo: buildAuthCallbackUrl(returnTo, "recovery") });
      if (error && !["user_not_found", "email_not_found", "user_already_exists", "email_exists"].includes(error.code ?? "")) throw error;
      trackFunnelEvent(kind === "signup" ? "confirmation_resent" : "confirmation_sent", {
        source: "auth",
        mode: kind === "signup" ? "signup" : "recovery",
      });
      setFeedback("If this address is eligible, an email will arrive shortly. Check your inbox and spam folder.");
      setShowConfirmation(true);
      setShowResetPassword(false);
    } catch (error) {
      trackFunnelEvent("auth_error", {
        source: "auth",
        mode: kind === "signup" ? "signup" : "recovery",
        stage: kind === "signup" ? "confirmation" : "signin",
        error_category: isRateLimited(error) ? "rate_limited" : "unknown",
      });
      if (isRateLimited(error)) {
        setPending(setPendingAuth({ ...attempt, resendAvailableAt: getAuthRetryAt(error) }));
        setNow(Date.now());
        setFeedback("Too many email requests. Please wait before requesting another link.");
      } else {
        setFeedback("We couldn't request an email. Please try again later. If you already confirmed your email, sign in.");
      }
    } finally {
      requestInFlight.current = false;
      setResetLoading(false);
    }
  };

  const handleResetPassword = () => requestEmail("recovery");

  const goToSignIn = async () => {
    try {
      if (user) await signOut();
      completePasswordRecovery();
      setShowConfirmation(false);
      setCallbackError(false);
      setPassword("");
      nav(buildAuthUrl(returnTo, "signin"), { replace: true });
    } catch { setFeedback("Unable to sign out. Please try again."); }
  };

  const handleUpdatePassword = async () => {
    if (requestInFlight.current || authLoading || !user || assuranceStatus !== "verified" || callbackError) return;
    const parsed = authSchema.shape.password.safeParse(newPassword);
    if (!parsed.success) { setFeedback(parsed.error.issues[0].message); return; }
    if (newPassword !== confirmNewPassword) { setFeedback("Passwords do not match."); return; }
    requestInFlight.current = true;
    setUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: parsed.data });
      if (error) throw error;
      toast.success("Password updated.");
      setNewPassword("");
      setConfirmNewPassword("");
      clearPendingAuth();
      completePasswordRecovery();
      nav(returnTo, { replace: true });
    } catch {
      setFeedback("Unable to update your password. Use a different password or request a new recovery link if this one expired.");
    } finally {
      requestInFlight.current = false;
      setUpdatingPassword(false);
    }
  };

  const verifyMFA = async () => {
    if (isVerifyingMFA.current || !/^\d{6}$/.test(mfaCode) || Date.now() < retryAt) return;
    setMFALoading(true);
    isVerifyingMFA.current = true;
    setFeedback("");
    try {
      const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
      if (factorsError) throw factorsError;
      const factor = factors.totp.find(item => item.status === "verified");
      if (!factor) throw new Error("Verified authenticator unavailable");
      // Create a fresh challenge on each attempt so expired challenges are recoverable.
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: factor.id });
      if (challengeError) throw challengeError;
      const { error } = await supabase.auth.mfa.verify({ factorId: factor.id, challengeId: challenge.id, code: mfaCode });
      if (error) throw error;
      await refreshAssurance();
      setMFACode("");
    } catch (error) {
      trackFunnelEvent("auth_error", {
        source: "auth",
        mode: "signin",
        stage: "mfa",
        error_category: isRateLimited(error) ? "rate_limited" : "invalid_credentials",
      });
      if (isRateLimited(error)) setRetryAt(getAuthRetryAt(error));
      setFeedback("Unable to verify the code. Try a fresh authenticator code, or wait if you made too many attempts.");
      setMFACode("");
    } finally {
      setMFALoading(false);
      isVerifyingMFA.current = false;
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container py-8 md:py-16">
        <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-20">
          {/* Brand panel (desktop only) */}
          <section className="hidden lg:block">
            <div className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-[hsl(var(--paper-edge))] bg-card px-4 py-2 shadow-sm">
              <span className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: "var(--gradient-gold)" }}>
                <Target className="h-4 w-4 text-[hsl(var(--accent-foreground))]" />
              </span>
              <span className="text-sm font-bold tracking-wide">BountyClaimer</span>
            </div>
            <h1 className="font-display text-5xl xl:text-6xl font-bold leading-[1.06] tracking-tight">
              Post a task.
              <br />
              Get it done.
              <br />
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: "var(--gradient-gold)" }}>
                Get paid.
              </span>
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">
              BountyClaimer matches your tasks with trusted doers. You fund the
              reward up front — it's held in escrow until you approve the proof.
            </p>
            <ul className="mt-10 space-y-6">
              <li className="flex items-start gap-4">
                <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[hsl(var(--accent))]/25 bg-[hsl(var(--accent))]/10">
                  <ShieldCheck className="h-5 w-5 text-[hsl(var(--accent))]" />
                </span>
                <div>
                  <div className="font-bold">Escrow-backed rewards</div>
                  <p className="mt-0.5 text-sm text-muted-foreground">Your money is held safely until you approve the submitted work.</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[hsl(var(--accent))]/25 bg-[hsl(var(--accent))]/10">
                  <Target className="h-5 w-5 text-[hsl(var(--accent))]" />
                </span>
                <div>
                  <div className="font-bold">Post in seconds</div>
                  <p className="mt-0.5 text-sm text-muted-foreground">Describe the task, set a reward, and publish. Claimants arrive in minutes.</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[hsl(var(--accent))]/25 bg-[hsl(var(--accent))]/10">
                  <Wallet className="h-5 w-5 text-[hsl(var(--accent))]" />
                </span>
                <div>
                  <div className="font-bold">Pay only on results</div>
                  <p className="mt-0.5 text-sm text-muted-foreground">Review the proof, then release the reward. No results, no payment.</p>
                </div>
              </li>
            </ul>
          </section>

          {/* Auth card */}
          <section>
            <div className="poster p-6 sm:p-10">
              {feedback && (
                <p role="status" className="mb-5 rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm">
                  {feedback}
                </p>
              )}
              {callbackError && (
                <div role="alert" className="mb-5 space-y-3 rounded-xl border border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5 px-4 py-3">
                  <p className="text-sm">This email link is invalid or expired. Request a new link below, or sign in if you already confirmed your email.</p>
                  <Button variant="link" className="h-auto p-0" onClick={goToSignIn}>Back to sign in</Button>
                </div>
              )}
              {user && assuranceStatus === "error" && (
                <div role="alert" className="mb-5 space-y-3 rounded-xl border border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5 px-4 py-3">
                  <p className="text-sm">We couldn't verify your session security. Please retry or sign out.</p>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => refreshAssurance()}>Retry security check</Button>
                    <Button variant="link" onClick={goToSignIn}>Sign out</Button>
                  </div>
                </div>
              )}
              {/* Password Reset Mode - Shows when user clicks email link */}
              {isPasswordResetMode ? (
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl" style={{ background: "var(--gradient-gold)" }}>
                      <Target className="h-8 w-8 text-[hsl(var(--accent-foreground))]" />
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold">Set New Password</h1>
                    <p className="mt-2 text-sm text-muted-foreground">Enter your new password below</p>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-password">New Password</Label>
                      <Input
                        id="new-password"
                        autoComplete="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        maxLength={72}
                        className="h-12"
                        autoFocus
                      />
                      <p className="text-xs text-muted-foreground">Must be at least 6 characters</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirm Password</Label>
                      <Input
                        id="confirm-password"
                        autoComplete="new-password"
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        placeholder="Confirm new password"
                        maxLength={72}
                        className="h-12"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleUpdatePassword();
                        }}
                      />
                    </div>

                    <Button
                      className="w-full h-12 font-bold"
                      onClick={handleUpdatePassword}
                      disabled={updatingPassword || authLoading || !user || assuranceStatus !== "verified" || callbackError || !newPassword || !confirmNewPassword}
                    >
                      {updatingPassword ? 'Updating...' : 'Update Password'}
                    </Button>

                    <Button
                      variant="link"
                      className="w-full text-sm text-muted-foreground"
                      onClick={goToSignIn}
                    >
                      Back to Sign In
                    </Button>
                    {!authLoading && (!user || callbackError) && <p role="alert">Open a valid recovery link to set a password. You can request a new link below.</p>}
                    <Button variant="outline" className="w-full h-11" onClick={() => setShowResetPassword(true)}>Request a new recovery link</Button>
                  </div>
                </div>
              ) : showConfirmation && pending?.email ? (
                <div className="space-y-6 py-4 text-center">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[hsl(var(--accent))]/10">
                    <Target className="h-8 w-8 text-[hsl(var(--accent))]" />
                  </div>
                  <div className="space-y-3">
                    <h1 className="font-display text-3xl sm:text-4xl font-bold">Check your email</h1>
                    <p className="mx-auto max-w-md text-sm leading-relaxed">
                      If <span className="font-semibold">{pending.email}</span> is eligible, you'll receive {pending.kind === "recovery" ? "a password reset" : "an account confirmation"} link. Check your inbox and spam folder.
                    </p>
                    <p className="text-sm text-muted-foreground">Already confirmed? Sign in to continue. This reminder is kept on this browser for up to 24 hours; email links may expire sooner.</p>
                  </div>
                  <Button className="w-full h-12 font-bold" disabled={resetLoading || resendSeconds > 0} onClick={() => requestEmail(pending.kind === "recovery" ? "recovery" : "signup", pending.email)}>
                    {resetLoading ? "Requesting..." : resendSeconds > 0 ? `Resend in ${resendSeconds}s` : "Resend email"}
                  </Button>
                  <Button variant="outline" className="w-full h-11" onClick={() => {
                    setShowConfirmation(false); setCallbackError(false); setPassword("");
                    setEmail(pending.email ?? ""); setEmailWarning("");
                    if (pending.kind === "recovery") setShowResetPassword(true);
                    else nav(buildAuthUrl(returnTo, "signup"), { replace: true });
                  }}>Edit email address</Button>
                  <Button variant="link" className="w-full" onClick={goToSignIn}>Continue to sign in</Button>
                </div>
              ) : (
                // Normal Auth Mode
                <>
                  <div className="mb-8 text-center">
                    <div
                      className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl"
                      style={{ background: "var(--gradient-gold)", boxShadow: "0 14px 34px -14px hsl(38 92% 50% / 0.55)" }}
                    >
                      <Target className="h-8 w-8 text-[hsl(var(--accent-foreground))]" />
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">{mode === "up" ? "Join BountyClaimer" : "Welcome back"}</h1>
                    <p className="mx-auto max-w-sm text-sm leading-relaxed text-muted-foreground">
                      {mode === "up"
                        ? "Create your free account to post tasks and earn rewards."
                        : "Sign in to post tasks, approve submitted proof, and release rewards."}
                    </p>
                  </div>

                  {/* Sign in / Sign up segmented switcher */}
                  <Tabs value={mode} onValueChange={value => {
                    setEmailWarning(""); setFeedback("");
                    nav(buildAuthUrl(returnTo, value === "in" ? "signin" : "signup"), { replace: true });
                  }}>
                    <TabsList className="mb-8 grid w-full grid-cols-2 gap-1.5 rounded-2xl border border-[hsl(var(--paper-edge))] bg-muted/50 p-1.5">
                      <TabsTrigger
                        value="in"
                        className="h-11 rounded-xl text-[15px] font-bold tracking-wide transition-all duration-200 data-[state=active]:[background:var(--gradient-gold)] data-[state=active]:text-[hsl(var(--accent-foreground))] data-[state=active]:shadow-md data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground hover:text-foreground focus-visible:ring-0"
                      >
                        Sign in
                      </TabsTrigger>
                      <TabsTrigger
                        value="up"
                        className="h-11 rounded-xl text-[15px] font-bold tracking-wide transition-all duration-200 data-[state=active]:[background:var(--gradient-gold)] data-[state=active]:text-[hsl(var(--accent-foreground))] data-[state=active]:shadow-md data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground hover:text-foreground focus-visible:ring-0"
                      >
                        Sign up
                      </TabsTrigger>
                    </TabsList>
                    
                    {(["in", "up"] as const).map((m) => (
                      <TabsContent key={m} value={m} className="space-y-5">
                        {/* Email Field */}
                        <div className="space-y-2">
                          <Label htmlFor={`email-${m}`} className="text-sm font-semibold">
                            Email Address
                          </Label>
                          <div className="relative">
                            <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground" />
                            <Input 
                              id={`email-${m}`} 
                              type="email" 
                              value={email} 
                              onChange={(e) => {
                                setEmail(e.target.value);
                                if (m === 'up') {
                                  setEmailWarning(e.target.value ? (!isAllowedEmail(e.target.value) ? 'This email domain is not allowed. Please use a valid email address.' : '') : '');
                                }
                              }} 
                              placeholder="your@email.com"
                              maxLength={255}
                              className="h-12 rounded-xl pl-11 text-base focus:border-[hsl(var(--accent))] focus:ring-[hsl(var(--accent))]/30"
                              autoComplete="email"
                            />
                          </div>
                          {m === 'up' && emailWarning && (
                            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              {emailWarning}
                            </p>
                          )}
                        </div>
                        
                        {/* Password Field with Toggle */}
                        <div className="space-y-2">
                          <Label htmlFor={`pw-${m}`} className="text-sm font-semibold">
                            Password
                          </Label>
                          <div className="relative">
                            <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground" />
                            <Input 
                              id={`pw-${m}`} 
                              type={showPassword ? "text" : "password"} 
                              value={password} 
                              onChange={handlePasswordChange} 
                              placeholder="Enter your password"
                              maxLength={72}
                              className="h-12 rounded-xl pl-11 pr-11 text-base focus:border-[hsl(var(--accent))] focus:ring-[hsl(var(--accent))]/30"
                              autoComplete={m === "in" ? "current-password" : "new-password"}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                              aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                              {showPassword ? (
                                <EyeOff className="h-[18px] w-[18px]" />
                              ) : (
                                <Eye className="h-[18px] w-[18px]" />
                              )}
                            </button>
                          </div>
                          
                          {/* Password Strength Indicator - Only for Sign Up */}
                          {m === "up" && password && (
                            <div className="space-y-2 rounded-xl border border-border bg-muted/30 p-3.5 pt-3">
                              <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((level) => (
                                  <div
                                    key={level}
                                    className={`h-1.5 flex-1 rounded-full transition-colors ${
                                      passwordStrength >= level
                                        ? passwordStrength <= 2
                                          ? 'bg-red-500'
                                          : passwordStrength === 3
                                          ? 'bg-yellow-500'
                                          : passwordStrength === 4
                                          ? 'bg-green-500'
                                          : 'bg-green-600'
                                        : 'bg-muted'
                                    }`}
                                  />
                                ))}
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Password strength</span>
                                <span className={`font-medium ${
                                  passwordStrength <= 2 ? 'text-red-500' :
                                  passwordStrength === 3 ? 'text-yellow-500' :
                                  'text-green-500'
                                }`}>
                                  {passwordStrength <= 2 ? 'Weak' :
                                   passwordStrength === 3 ? 'Fair' :
                                   passwordStrength === 4 ? 'Strong' : 'Very Strong'}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-1.5 text-xs">
                                {[
                                  { test: password.length >= 8, label: '8+ characters' },
                                  { test: /[a-z]/.test(password) && /[A-Z]/.test(password), label: 'Mixed case' },
                                  { test: /\d/.test(password), label: 'Numbers' },
                                  { test: /[^a-zA-Z0-9]/.test(password), label: 'Symbols' },
                                ].map((req, idx) => (
                                  <div key={idx} className="flex items-center gap-1.5">
                                    {req.test ? (
                                      <Check className="h-3 w-3 text-green-500" />
                                    ) : (
                                      <X className="h-3 w-3 text-muted-foreground" />
                                    )}
                                    <span className={req.test ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}>
                                      {req.label}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Submit Button */}
                        <Button 
                          className="w-full h-12 rounded-xl text-base font-bold shadow-md hover:opacity-90 hover:shadow-lg"
                          style={{ background: "var(--gradient-gold)", color: "hsl(var(--accent-foreground))" }}
                          disabled={loading || authLoading || !!user || now < retryAt || (m === "up" && resendSeconds > 0)}
                          onClick={() => submit(m)}
                        >
                          {loading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              {m === "in" ? "Signing in..." : "Creating account..."}
                            </>
                          ) : (
                            m === "in" ? "Sign in" : "Create account"
                          )}
                        </Button>
                        
                        {m === "up" && resendSeconds > 0 && <p role="status" className="text-sm">Please wait {resendSeconds}s before requesting another account email.</p>}
                        {m === "in" && (
                          <Button variant="link" className="w-full" disabled={resetLoading || resendSeconds > 0} onClick={() => requestEmail("signup", email)}>
                            {resendSeconds > 0 ? `Resend confirmation in ${resendSeconds}s` : "Resend confirmation email"}
                          </Button>
                        )}
                        {/* Forgot Password Link */}
                        {m === "in" && (
                          <Button 
                            variant="link" 
                            className="w-full text-sm text-muted-foreground hover:text-foreground" 
                            onClick={() => setShowResetPassword(true)}
                          >
                            Forgot password?
                          </Button>
                        )}
                      </TabsContent>
                    ))}
                  </Tabs>
                </>
              )}
            </div>
            <p className="mt-5 text-center text-xs text-muted-foreground">
              Your email is used only for account access. We never sell your data.
            </p>
          </section>
        </div>
      </main>

      {/* MFA Verification Dialog - Simple inline modal */}
      {showMFADialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" style={{ display: 'flex' }}>
          <div role="dialog" aria-modal="true" aria-label="Two-Factor Authentication" className="bg-background border rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Two-Factor Authentication</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Enter the 6-digit code from your authenticator app to complete sign in.
            </p>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mfa-code">Authentication Code</Label>
                <Input
                  id="mfa-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(e) => setMFACode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-widest"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') verifyMFA();
                  }}
                  autoFocus
                />
              </div>

              {feedback && <p role="alert" className="text-sm">{feedback}</p>}
              {now < retryAt && <p role="status">Please wait {Math.ceil((retryAt - now) / 1000)}s before trying again.</p>}

              <Button
                className="w-full"
                onClick={verifyMFA}
                disabled={mfaLoading || mfaCode.length !== 6 || now < retryAt}
              >
                {mfaLoading ? 'Verifying...' : 'Verify & Continue'}
              </Button>
              <Button variant="link" className="w-full" disabled={mfaLoading} onClick={goToSignIn}>Cancel and sign out</Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Dialog */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" style={{ display: 'flex' }}>
          <div role="dialog" aria-modal="true" aria-label="Reset Password" className="bg-background border rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-2 mb-4">
              <Target className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Reset Password</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email Address</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
              </div>

              {feedback && <p role="status" className="text-sm">{feedback}</p>}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowResetPassword(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleResetPassword}
                  disabled={resetLoading || !email || resendSeconds > 0}
                >
                  {resetLoading ? 'Requesting...' : resendSeconds > 0 ? `Wait ${resendSeconds}s` : 'Send Reset Link'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Auth;
